import PropTypes from 'prop-types';
import React from 'react';
import { UX2, constants } from '@wsb/guac-widget-core';
import { ProductStarRating } from './Yotpo';
import dataAids from '../../../constants/dataAids';
import {
  OlsConfigStore,
  RouteHandler,
  ShopViewConstants,
  ScrollWidgetActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import {
  CategoryListStore,
  ShopStore
} from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import { NumberFormatter } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { hasSalePrice, calculateSalePercentage, formatPrice } from '../utils/ProductUtils';
import * as Metrics from '../utils/trackMetric';
import * as MetricEvents from '../../../constants/metrics';
import eyebrowKeys from '../../../constants/widget/products/ProductListItem/eyebrowKeys';
import { mapContextToProps } from '../../components/OlsShopContext';
import {
  saleRibbonOptions,
  bestsellerRibbonOptions,
  newProductRibbonOptions
} from '../../../constants/smartRibbonOptions';
import QuickView from './QuickView';

export class ProductListItem extends React.Component {
  constructor() {
    super(...arguments);

    this.state = { quickViewIsOpen: false };

    this.handleViewProductDetailClick = this.handleViewProductDetailClick.bind(this);
    this.handleQuickViewClick = this.handleQuickViewClick.bind(this);
  }

  componentDidMount() {
    const { product, metrics } = this.props;

    if (!product.in_stock) {
      Metrics.track(MetricEvents.OUT_OF_STOCK_PRODUCT_IMPRESSION);
    }

    if (this.isSmartRibbonShown) {
      Metrics.track(metrics.smartRibbonImpression);
    }

    if (this.displayedEyebrowKey) {
      this.trackEyebrowImpression();
    }
  }

  trackEyebrowImpression() {
    const { metrics } = this.props;

    const eyebrowMap = {
      [eyebrowKeys.LIMITED_EDITION]: metrics.limitedEditionEyebrowImpression,
      [eyebrowKeys.FREE_SHIPPING]: metrics.freeShippingEyebrowImpression,
      [eyebrowKeys.FREE_SHIPPING_AND_RETURNS]: metrics.freeShippingAndReturnsEyebrowImpression,
      [eyebrowKeys.FREE_RETURNS]: metrics.freeReturnsEyebrowImpression
    };

    Metrics.track(eyebrowMap[this.displayedEyebrowKey]);
  }

  trackEyebrowProductClick() {
    const { metrics } = this.props;

    const eyebrowMap = {
      [eyebrowKeys.LIMITED_EDITION]: metrics.limitedEditionEyebrowClick,
      [eyebrowKeys.FREE_SHIPPING]: metrics.freeShippingEyebrowClick,
      [eyebrowKeys.FREE_SHIPPING_AND_RETURNS]: metrics.freeShippingAndReturnsEyebrowClick,
      [eyebrowKeys.FREE_RETURNS]: metrics.freeReturnsEyebrowClick
    };

    Metrics.track(eyebrowMap[this.displayedEyebrowKey]);
  }

  get isSmartRibbonShown() {
    const { smartRibbonsSale, smartRibbonsBestseller, smartRibbonsNewProduct } = this.props;

    return smartRibbonsSale || smartRibbonsBestseller || smartRibbonsNewProduct;
  }

  get displayedSmartRibbonKey() {
    const {
      smartRibbonsSale,
      smartRibbonsBestseller,
      smartRibbonsNewProduct,
      selectedSaleRibbon,
      selectedBestsellerRibbon,
      selectedNewProductRibbon,
      product
    } = this.props;

    // This enforces the smart ribbon priority
    if (smartRibbonsSale && product['on_sale?']) return selectedSaleRibbon;
    if (smartRibbonsBestseller && product['bestseller?']) return selectedBestsellerRibbon;
    if (smartRibbonsNewProduct && product['new_product?']) return selectedNewProductRibbon;

    return null;
  }

  get smartRibbonText() {
    const { translate = () => {} } = this.props;

    const saleRibbonOptionsMap = {
      [saleRibbonOptions.SALE]: translate('products.sale_label'),
      [saleRibbonOptions.SAVE_X_PERCENT]: translate('products.save_percentage', {
        percentage: this.salePercentageText
      }),
      [saleRibbonOptions.X_PERCENT_OFF]: translate('products.percentage_off', {
        percentage: this.salePercentageText
      }),
      [bestsellerRibbonOptions.BESTSELLER]: translate('products.bestseller'),
      [bestsellerRibbonOptions.TOP_PICK]: translate('products.top_pick'),
      [bestsellerRibbonOptions.HOT_ITEM]: translate('products.hot_item'),
      [newProductRibbonOptions.NEW]: translate('products.new'),
      [newProductRibbonOptions.NEW_ARRIVAL]: translate('products.new_arrival')
    };

    return saleRibbonOptionsMap[this.displayedSmartRibbonKey];
  }

  get currentTaxons() {
    const { selectedTaxonPermalink } = ShopStore.getState();
    if ('getTaxonListByPermalink' in CategoryListStore) {
      return CategoryListStore.getTaxonListByPermalink(selectedTaxonPermalink);
    }
    return [];
  }

  handleViewProductDetailClick(e) {
    e.preventDefault();

    if (this.state.quickViewIsOpen) {
      this.setState({ quickViewButton: false });
      return;
    }

    const { product, metrics } = this.props;

    if (!product.in_stock) {
      Metrics.track(MetricEvents.OUT_OF_STOCK_PRODUCT_CLICK);
    }

    if (this.isSmartRibbonShown) {
      Metrics.track(metrics.smartRibbonClick);
    }

    if (this.displayedEyebrowKey) {
      this.trackEyebrowProductClick();
    }

    Metrics.track(metrics.click);
    RouteHandler.navigate(ShopViewConstants.PRODUCT_DETAIL, {
      slug: product.slug,
      taxonSource: this.currentTaxons
    });
    ScrollWidgetActions.scrollShopWidget();
  }

  handleQuickViewClick(value) {
    const { metrics } = this.props;
    Metrics.track(metrics.quickViewButtonClick);

    this.setState({ quickViewIsOpen: value });
  }

  get salePercentageText() {
    const { product } = this.props;

    if (!hasSalePrice(product)) return null;

    const { sale_price, price } = product;
    const percentage = calculateSalePercentage(price, sale_price);
    if (percentage < 1) return null;

    return NumberFormatter.toFormattedPercentage(percentage, 0, 0);
  }

  determinePrice(price) {
    return !['object', 'undefined'].includes(typeof price)
      ? formatPrice({ currency: true, number: +price })
      : price;
  }

  get displayedEyebrowKey() {
    const { product } = this.props;
    // This keeps track of the eyebrow priority
    if (product.limited_edition) return eyebrowKeys.LIMITED_EDITION;
    if (product.ship_free && product.free_return) return eyebrowKeys.FREE_SHIPPING_AND_RETURNS;
    if (product.ship_free) return eyebrowKeys.FREE_SHIPPING;
    if (product.free_return) return eyebrowKeys.FREE_RETURNS;
    return null;
  }

  get eyebrowText() {
    const { translate = () => {} } = this.props;

    const eyebrowMap = {
      [eyebrowKeys.FREE_RETURNS]: translate('products.free_returns'),
      [eyebrowKeys.LIMITED_EDITION]: translate('products.limited_edition'),
      [eyebrowKeys.FREE_SHIPPING]: translate('products.free_shipping'),
      [eyebrowKeys.FREE_SHIPPING_AND_RETURNS]: translate('products.free_shipping_and_returns')
    };

    return eyebrowMap[this.displayedEyebrowKey];
  }

  get footer() {
    const { product, translate = () => {} } = this.props;
    const { variant_count, in_stock } = product;
    const hasVariants = variant_count >= 2;

    if (!in_stock) {
      return {
        footerText: translate('products.item_out_of_stock_v2'),
        footerAlert: true
      };
    }

    if (hasVariants) {
      return {
        footerText: translate('products.more_options'),
        footerAlert: false
      };
    }

    return { footerAlert: false, footerText: null };
  }

  get productCardData() {
    const {
      product,
      isRatingShown,
      renderMode,
      cardSize,
      translate,
      imageCropMethod,
      imageShape,
      galleryImageShape,
      galleryShowBorder,
      smartRibbonStyle,
      carouselWidthOffset,
      canUseCarousel,
      canUseQuickView
    } = this.props;
    const { name, id, product_type, default_asset_url, has_price_range, image_list } = product;
    const price = this.determinePrice(product.price);
    const salePrice = hasSalePrice(product) ? this.determinePrice(product.sale_price) : null;
    const { footerText, footerAlert } = this.footer;

    let ratings;
    if (isRatingShown) {
      const ratingsContainerStyles = {
        textAlign: 'center'
      };

      ratings = (
        <ProductStarRating
          productSlug={ product.slug }
          containerStyle={ ratingsContainerStyles }
          containerClass='product-list'
          renderMode={ renderMode }
        />
      );
    }

    let imageList;
    if (image_list && image_list.length > 1 && canUseCarousel) {
      imageList = image_list.map(image => image.url);
    }

    return {
      id,
      name,
      footerText,
      footerAlert,
      productType: product_type,
      price: price?.display || price,
      salePrice: salePrice?.display || salePrice,
      imageUrl: default_asset_url || OlsConfigStore.getState().noProductImage,
      imageList,
      carouselWidthOffset,
      ribbonText: this.smartRibbonText,
      ribbonType: smartRibbonStyle,
      eyebrowText: this.eyebrowText,
      imageCropMethod,
      imageShape: galleryImageShape || imageShape,
      hasImageBorder: galleryShowBorder,
      priceText: has_price_range ? translate('products.from') : null,
      ratings,
      colors: product.colors,
      renderColorsLabel: ({ remainder }) => translate('products.plus_num', { num: remainder }),
      cardSize,
      hoverButtonText: canUseQuickView ? translate('products.quick_view') : null,
      onHoverButtonClick: () => {
        this.handleQuickViewClick(true);
      }
    };
  }

  render() {
    const { product, renderMode, canUseQuickView, pdpImageShape, pdpImageCropMethod } = this.props;
    const { quickViewIsOpen } = this.state;
    const { Block, Link } = UX2.Element;

    const getLinkProps = this.props.getLinkProps || (() => {});
    const linkProps = getLinkProps(ShopViewConstants.PRODUCT_DETAIL, { slug: product.slug });
    const { name, slug } = product;

    const isEditMode = renderMode === constants.renderModes.EDIT;

    const productDataAids = {
      name: `${dataAids.PRODUCT_CARD_NAME_RENDERED}_${name}`,
      price: `${dataAids.PRODUCT_PRICE_RENDERED}_${name}`,
      image: `${dataAids.PRODUCT_IMAGE_RENDERED}_${name}`,
      salePrice: `${dataAids.PRODUCT_SALE_PRICE_RENDERED}_${name}`,
      eyebrow: `${dataAids.PRODUCT_EYEBROW_RENDERED}_${name}`,
      footer: `${dataAids.PRODUCT_FOOTER_RENDERED}_${name}`,
      ribbon: `${dataAids.PRODUCT_RIBBON_RENDERED}_${name}`,
      carousel: `${dataAids.PRODUCT_CAROUSEL_RENDERED}_${name}`,
      colorSwatches: `${dataAids.PRODUCT_COLOR_SWATCHES_RENDERED}_${name}`,
      quickViewButton: `${dataAids.PRODUCT_QUICK_VIEW_BUTTON_RENDERED}_${name}`,
      quickViewModal: `${dataAids.PRODUCT_QUICK_VIEW_MODAL_RENDERED}_${name}`
    };

    return (
      <Block>
        <Link
          data-aid={ `${dataAids.PRODUCT_NAME_RENDERED}_${name}` }
          onClick={ !isEditMode && !quickViewIsOpen && this.handleViewProductDetailClick }
          href='javascript:;'
          { ...linkProps }
        >
          <UX2.Component.CommerceCardItem { ...this.productCardData } dataAids={ productDataAids } />
        </Link>
        { canUseQuickView && quickViewIsOpen && (
          <QuickView
            { ...this.props }
            dataAid={ productDataAids.quickViewModal }
            productSlug={ slug }
            setQuickViewIsOpen={ this.handleQuickViewClick }
            smartRibbonsBestsellerOption={ this.props.selectedBestsellerRibbon }
            smartRibbonsNewProductOption={ this.props.selectedNewProductRibbon }
            pdpImageShape={ pdpImageShape }
            pdpImageCropMethod={ pdpImageCropMethod }
          />
        ) }
      </Block>
    );
  }
}

ProductListItem.propTypes = {
  product: PropTypes.object.isRequired,
  renderMode: PropTypes.string,
  imageShape: PropTypes.string,
  galleryShowBorder: PropTypes.bool,
  imageCropMethod: PropTypes.string,
  smartRibbonsSale: PropTypes.bool,
  smartRibbonsBestseller: PropTypes.bool,
  smartRibbonsNewProduct: PropTypes.bool,
  selectedSaleRibbon: PropTypes.string,
  selectedBestsellerRibbon: PropTypes.string,
  selectedNewProductRibbon: PropTypes.string,
  isRatingShown: PropTypes.bool,
  cardSize: PropTypes.object,
  metrics: PropTypes.shape({
    click: PropTypes.object,
    smartRibbonImpression: PropTypes.object,
    smartRibbonClick: PropTypes.object,
    limitedEditionEyebrowImpression: PropTypes.object,
    limitedEditionEyebrowClick: PropTypes.object,
    freeShippingEyebrowImpression: PropTypes.object,
    freeShippingEyebrowClick: PropTypes.object,
    freeShippingAndReturnsEyebrowImpression: PropTypes.object,
    freeShippingAndReturnsEyebrowClick: PropTypes.object,
    freeReturnsEyebrowImpression: PropTypes.object,
    freeReturnsEyebrowClick: PropTypes.object,
    quickViewButtonClick: PropTypes.object
  }),
  getLinkProps: PropTypes.func,
  translate: PropTypes.func,
  galleryImageShape: PropTypes.string,
  smartRibbonStyle: PropTypes.string,
  carouselWidthOffset: PropTypes.string,
  canUseCarousel: PropTypes.bool,
  canUseQuickView: PropTypes.bool,
  pdpImageShape: PropTypes.string,
  pdpImageCropMethod: PropTypes.string
};

ProductListItem.defaultProps = {
  isRatingShown: true
};

export default mapContextToProps(ProductListItem, context => ({
  getLinkProps: context.getLinkProps,
  translate: context.translate
}));
